import React from "react";
import { ShieldCheck, Award, Users, Target, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { PageHero } from "./PageHero";
import { mockData } from "../mock";

const values = [
  { icon: <ShieldCheck className="h-7 w-7" />, title: "Safety First", description: "Every project prioritizes safety with comprehensive protocols and experienced crew members." },
  { icon: <Award className="h-7 w-7" />, title: "Veteran Excellence", description: "Military precision and discipline applied to every demolition and site work project." },
  { icon: <Users className="h-7 w-7" />, title: "Expert Team", description: "Highly trained professionals with years of demolition and construction experience." },
  { icon: <Target className="h-7 w-7" />, title: "Precision Work", description: "Accurate, efficient demolition that meets timelines and exceeds expectations." },
];

const stats = [
  { value: "25+", label: "Projects Completed" },
  { value: "100+", label: "Project Involvement" },
  { value: "100%", label: "Safety Record" },
];

export const AboutPage = () => {
  const navigate = useNavigate();

  return (
    <div data-testid="about-page">
      <PageHero
        kicker="About Robo Demo LLC"
        title="Service-Disabled"
        highlight="Veteran Owned"
        subtitle="Bringing military precision and an unwavering commitment to every demolition and site work project — primarily across the DMV area with nationwide capabilities."
      />

      {/* Mission */}
      <section className="tex-plate py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="label-kicker mb-4">Our Mission</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold uppercase tracking-tight text-white leading-none mb-8">
              Built On Discipline
            </h2>
            <div className="space-y-5 text-gray-400 leading-relaxed">
              <p>
                Robo Demo LLC is a proud service-disabled veteran-owned small
                business founded in August 2023, bringing military precision and
                an unwavering commitment to every project we take on.
              </p>
              <p>
                With years of experience in both residential and commercial
                demolition, we understand that every job requires careful
                planning, strict safety protocols, and expert execution. Our team
                combines military discipline with industry expertise to deliver
                results that exceed expectations.
              </p>
              <p>
                From small residential teardowns to large commercial demolition
                and comprehensive site clearing, we handle projects of all sizes
                with the same dedication to excellence that defines our military
                heritage.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-10">
              {stats.map((s) => (
                <div key={s.label} className="card-industrial p-5 text-center">
                  <div className="font-display text-3xl font-black text-primary">{s.value}</div>
                  <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="absolute -top-4 -left-4 w-24 h-24 border-t-2 border-l-2 border-primary" />
            <img src={mockData.projects[0].images[0]} alt="Robo Demo industrial steel building demolition project" className="w-full h-[520px] object-cover" />
            <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b-2 border-r-2 border-primary" />
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 tex-grid opacity-60" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="label-kicker mb-4">What Drives Us</p>
          <h2 className="font-display text-4xl md:text-5xl font-bold uppercase tracking-tight text-white leading-none mb-12">
            Our Core Values
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
            {values.map((v) => (
              <div key={v.title} className="bg-card p-8" data-testid={`about-value-${v.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="w-14 h-14 flex items-center justify-center border border-primary/30 bg-primary/10 text-primary mb-5">{v.icon}</div>
                <h3 className="font-display text-xl font-bold uppercase tracking-tight text-white mb-2">{v.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{v.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="tex-plate py-20 border-t border-border">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-display text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mb-6">
            Ready To Work With Veterans?
          </h2>
          <p className="text-gray-400 text-lg mb-10 max-w-2xl mx-auto">
            Experience the difference that military precision and commitment make on your demolition project.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button data-testid="about-page-quote-btn" size="lg" onClick={() => navigate("/quote")} className="rounded-none font-bold uppercase tracking-widest px-8 py-6">
              Get Free Quote <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button data-testid="about-page-founders-btn" size="lg" variant="outline" onClick={() => navigate("/founders")} className="rounded-none font-bold uppercase tracking-widest px-8 py-6 border-white/30 bg-transparent text-white hover:bg-white hover:text-background">
              Meet Our Founders
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
